import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, TemplateRef, viewChild } from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { ButtonComponent } from '../../../../../../shared/_components/button/button.component';
import { Subject, Subscription } from 'rxjs';

// Subjects
import { MediaAssetService } from '../../../services/media-asset.service';
import { MediaUploadService } from '../../../services/media-upload.service';

// Models
import { ButtonShape, ButtonSize, ButtonVariant } from '../../../../../../shared/_model/button.model';
import { CurrentUploadOperation } from '../../../models/media-upload.model';
import { FlowEvent, FlowEventDirection, FlowEventType, MediaUploadFlowStep } from '../../../../../../shared/_model/flow.model';
import { GetAssetStatusResponse } from '@oddr/user-services';
import { Statuses } from '@oddr/user-schemas';
import { MediaThumbnailHelper } from '../../../../../../shared/_helpers/media-thumbnail.helper';

// Steps
import { STEP_INTEGRATION_FACEBOOK } from '../../steps/step-integration-facebook/step-integration-facebook.def';
import { STEP_INTEGRATION_LINKED_IN } from '../../steps/step-integration-linkedin/step-integration-linkedin.def';
import { STEP_INTEGRATION_YOUTUBE } from '../../steps/step-integration-youtube/step-integration-youtube.def';
import { STEP_INTEGRATION_X } from '../../steps/step-integration-x/step-integration-x.def';
import { STEP_INTEGRATION_INSTAGRAM } from '../../steps/step-integration-instagram/step-integration-instagram.def';
import { STEP_MEDIA_FILE_COMPLETE, STEP_MEDIA_IMAGE_COMPLETE } from '../../steps/step-media-image-complete/step-media-image-complete.def';
import { STEP_MEDIA_VIDEO_COMPLETE } from '../../steps/step-media-video-complete/step-media-video-complete.def';
import { STEP_MEDIA_VIDEO_INTEGRATIONS } from '../../steps/step-media-video-integrations/step-media-video-integrations.def';
import { STEP_MEDIA_VIDEO_TRANSCRIPTION } from '../../steps/step-media-video-transcription/step-media-video-transcription.def';
import { STEP_MEDIA_UPLOAD_FILE_SELECT } from '../../steps/step-media-upload-file-select/step-media-upload-file-select.def';

// Directives
import { StateHandlerDirective } from '../../../../../../core/_components/state-handler/state-handler.component';

// Providers whose configuration steps are titled "{Team}'s {Provider}" when
// uploading in a team context
import { IntegrationProvider } from '../../../../../models/integration-providers.model';

const PROVIDER_STEP_IDENTIFIERS = Object.values(IntegrationProvider) as string[];

@Component({
  selector: 'pp-flow-media-upload',
  templateUrl: './flow-media-upload.component.html',
  styleUrl: './flow-media-upload.component.scss',
  imports: [CommonModule, ButtonComponent],
})
export class FlowMediaUploadComponent extends StateHandlerDirective implements OnDestroy {

  readonly mediaUploadCancelConfirmationModal = viewChild<TemplateRef<any>>('mediaUploadCancelConfirmationModal');

  // Aliases
  buttonShape = ButtonShape;
  buttonSize = ButtonSize;
  buttonVariant = ButtonVariant;

  // Modal references
  cancellationModelRef?: NgbModalRef;

  // Navigation
  flowDefined = false;
  steps: MediaUploadFlowStep[] = [];
  selectedStep!: MediaUploadFlowStep | undefined;

  // Upload progress
  uploadProgress = 0;
  uploadComplete = false;
  processingComplete = false;

  // Services
  readonly activeModal = inject(NgbActiveModal);
  private readonly mediaAssetService = inject(MediaAssetService);
  private readonly mediaUploadService = inject(MediaUploadService);
  private readonly modalService = inject(NgbModal);

  // Subjects
  completeFlow$: Subject<void> = new Subject<void>();
  destroyFlow$: Subject<void> = new Subject<void>();
  navigateToNextStep$: Subject<string> = new Subject<string>();
  navigateToPreviousStep$: Subject<string> = new Subject<string>();
  resetFlow$: Subject<void> = new Subject<void>();

  // Subscriptions
  private currentUpload$: Subscription;
  private processingPollingStatus$: Subscription;
  private selectedIntegrationEvent$: Subscription;
  private uploadProgress$: Subscription;

  constructor() {
    super();

    // Define subscriptions
    this.completeFlow$.subscribe({
      next: () => this.completeFlow(),
    });

    this.destroyFlow$.subscribe({
      next: () => this.destroyFlow(),
    });

    this.navigateToNextStep$.subscribe({
      next: () => this.goToNextStep(),
    });

    this.navigateToPreviousStep$.subscribe({
      next: () => this.goToPreviousStep(),
    });

    this.resetFlow$.subscribe({
      next: () => this.resetFlow(),
    });

    // Dynamically build flow based on file type
    this.currentUpload$ = this.mediaUploadService.currentUpload$.subscribe({
      next: (response: CurrentUploadOperation | null) => {
        if (response && !this.flowDefined) {
          switch (true) {
            case response.contentType.startsWith('image/'):
              this.steps.push(
                STEP_MEDIA_VIDEO_INTEGRATIONS,
                STEP_MEDIA_IMAGE_COMPLETE,
              );
              break;
            case response.contentType.startsWith('video/'):
              this.steps.push(
                STEP_MEDIA_VIDEO_INTEGRATIONS,
                STEP_MEDIA_VIDEO_TRANSCRIPTION,
                STEP_MEDIA_VIDEO_COMPLETE,
              );
              break;

            // No provider takes a PDF or audio file: published to ODDR only, so
            // straight to review, which saves an empty integrations list
            case MediaThumbnailHelper.isGenerated(response.contentType):
              this.steps.push(STEP_MEDIA_FILE_COMPLETE);
              break;
          }
          this.flowDefined = true;
        }
      },
    });

    // Dynamically update flow based on user integration selection
    this.selectedIntegrationEvent$ = this.mediaUploadService.selectedIntegrationEvent$.subscribe({
      next: (event: FlowEvent) => {
        switch (event.eventType) {
          case FlowEventType.Add:
            this.addStepToFlow(event.id);
            break;
          case FlowEventType.Remove:
            this.removeStepFromFlow(event.id);
            break;
        }
      },
    });

    // Track upload progress
    this.uploadProgress$ = this.mediaUploadService.progressUploadIndicator$.subscribe({
      next: (progress: number) => {
        this.uploadProgress = Math.round(progress);
        if (progress >= 100 && !this.uploadComplete) {
          this.uploadComplete = true;
          if (!this.mediaUploadService.isGroupUpload) {
            this.mediaUploadService.startUploadPollingCheck();
          }
        }
      },
    });

    // Track processing status
    this.processingPollingStatus$ = this.mediaUploadService.processingPollingStatus$.subscribe({
      next: (status: GetAssetStatusResponse) => {
        if (status.c2paStatus === Statuses.Complete) {
          this.processingComplete = true;
        }
      },
    });

    // Setup Flow
    this.initiateFlow();
  }

  ngOnDestroy(): void {
    this.currentUpload$?.unsubscribe();
    this.uploadProgress$?.unsubscribe();
    this.processingPollingStatus$?.unsubscribe();
    this.navigateToNextStep$?.unsubscribe();
    this.navigateToPreviousStep$.unsubscribe();
    this.selectedIntegrationEvent$?.unsubscribe();
  }

  /**
   * The display name of the team when uploading in a team context, or null for
   * a personal upload.
   * @readonly
   * @type {string | null}
   * @memberof FlowMediaUploadComponent
   */
  get postingAsTeamName(): string | null {
    return this.viewStateService.selectedProfileDisplayName;
  }

  /**
   * The title of the selected step. In a team context the integrations and
   * provider steps name the team, so the user can confirm which account the
   * post goes through.
   * @readonly
   * @type {string}
   * @memberof FlowMediaUploadComponent
   */
  get stepTitle(): string {
    const teamName = this.postingAsTeamName;
    if (teamName && this.selectedStep) {
      if (this.selectedStep.identifier === STEP_MEDIA_VIDEO_INTEGRATIONS.identifier) return `Select integrations for ${teamName}`;
      if (PROVIDER_STEP_IDENTIFIERS.includes(this.selectedStep.identifier ?? '')) return `${teamName}'s ${this.selectedStep.name}`;
    }
    return this.selectedStep?.title ?? '';
  }

  /**
   * Adds an integration substep to the flow.
   * @param id the identifier of the integration to add.
   * @memberof FlowMediaUploadComponent
   */
  addStepToFlow(id: string): void {
    let stepToAdd: MediaUploadFlowStep | undefined = undefined;

    // Determine which step definiton to add
    switch (id) {
      case 'facebook':
        stepToAdd = STEP_INTEGRATION_FACEBOOK;
        break;
      case 'linkedin':
        stepToAdd = STEP_INTEGRATION_LINKED_IN;
        break;
      case 'youtube':
        stepToAdd = STEP_INTEGRATION_YOUTUBE;
        break;
      case 'x':
        stepToAdd = STEP_INTEGRATION_X;
        break;
      case 'instagram':
        stepToAdd = STEP_INTEGRATION_INSTAGRAM;
        break;
    }

    if (stepToAdd) {
      // Find the index of the last substep in the list of steps
      let entryPointIndex = this.steps.reduce((last, item, index) => (item.subStep ? index : last), -1);

      // If a substep index was not found, fall back to after the integrations step
      if (entryPointIndex === -1) {
        entryPointIndex = this.steps.findIndex(s => s.stepIdentifier === 'mediaUploadFlowStepVideoIntegrations');
      }

      // Add step to step list at desired index
      this.steps.splice(entryPointIndex + 1, 0, stepToAdd);
    }
  }

  /**
   * Closes the cancellation model and cancels the media upload.
   * @param destroyFlow if `true`, destroys the media upload flow.
   * @memberof FlowMediaUploadComponent
   */
  cancelFlow(destroyFlow: boolean): void {
    this.cancellationModelRef?.close();
    if (destroyFlow) this.destroyFlow();
  }

  /**
   * Gracefully completes the flow, resets the media upload service and closes the modal.
   * @memberof FlowMediaUploadComponent
   */
  completeFlow(): void {
    // Attempt to reload the lmedia list
    setTimeout(() => {
      this.mediaAssetService.getMediaList().subscribe();
      this.destroyFlow(); // Destroy the flow
    }, 2000); // Allows time for backend asset record to be finalised
  }

  /**
   * Destroys the flow state and all of its components.
   * @memberof FlowMediaUploadComponent
   */
  destroyFlow(): void {
    // Close the active modal
    this.activeModal.close();

    // Clear the upload service of any data relating to the upload. Set timeout allows time for modal to close before performing reset.
    setTimeout(() => this.resetFlow(), 500);
  }

  /**
   * Navigates to the next step in the flow.
   * @memberof FlowMediaUploadComponent
   */
  goToNextStep(): void {
    // Using the current selected step index, locate the next step in the list and set it as selected
    const currentStepIndex = this.steps.findIndex(s => s.stepIdentifier === this.selectedStep?.stepIdentifier);
    this.remediateFlowNavigationState(currentStepIndex + 1, FlowEventDirection.Forward);
  }

  /**
   * Navigates to the previous step in the flow.
   * @memberof FlowMediaUploadComponent
   */
  goToPreviousStep(): void {
    // Using the current selected step index, locate the previous step in the list and set it as selected
    const currentStepIndex = this.steps.findIndex(s => s.stepIdentifier === this.selectedStep?.stepIdentifier);
    this.remediateFlowNavigationState(currentStepIndex - 1, FlowEventDirection.Backward);
  }

  /**
   * Initiates the media upload flow.
   * @memberof FlowMediaUploadComponent
   */
  initiateFlow(): void {
    // Define select and upload steps by default
    this.steps = [
      STEP_MEDIA_UPLOAD_FILE_SELECT,
    ];

    // Add initial step configurations
    this.steps[0].active = true;

    // Set the initial selected step
    this.selectedStep = this.steps.find(s => s.active);
  }

  /**
   * Launches the confirmation modal when cancelling an upload.
   * @memberof FlowMediaUploadComponent
   */
  launchCancelConfirmationModal(): void {
    // Nothing chosen yet, so nothing to lose: close without asking
    if (!this.flowDefined) {
      this.destroyFlow();
      return;
    }

    // Launch the confirmation modal
    this.cancellationModelRef = this.modalService.open(this.mediaUploadCancelConfirmationModal(), {
      backdrop: 'static',
      centered: true,
    });
  }

  /**
   * Removes an integration substep from the flow.
   * @param id the identifier of the integration to remove.
   * @memberof FlowMediaUploadComponent
   */
  removeStepFromFlow(id: string): void {
    // Remove step returning current step list with removed id filtered out
    this.steps = this.steps.filter(s => s.identifier !== id);
  }

  /**
   * Resets the flow and aborts any uploads in progress.
   * @memberof FlowMediaUploadComponent
   */
  resetFlow(): void {
    // Abort all polling activities
    this.mediaUploadService.clearUploadOperation();

    // Re-intialise the flow
    this.initiateFlow();
  }

  /**
   * Given a new selected step order number, handles navigation and UI display updates for the navigation event.
   * @private
   * @param newStepIndex the index of the next selected step.
   * @param direction the direction the flow has traversed in (forward | back).
   * @memberof FlowMediaUploadComponent
   */
  private remediateFlowNavigationState(newStepIndex: number, direction: FlowEventDirection): void {
    // Find and allocate the next selected step
    this.selectedStep = this.steps[newStepIndex];

    // Set any steps before the selected step to completed (by order)
    if (direction === FlowEventDirection.Forward) {
      this.steps.forEach((step: MediaUploadFlowStep) => {
        const index = this.steps.indexOf(step);
        if (this.selectedStep !== undefined && index < newStepIndex) step.completed = true;
      });
    }
    if (direction === FlowEventDirection.Backward) {
      this.steps.forEach((step: MediaUploadFlowStep) => {
        const index = this.steps.indexOf(step);
        if (this.selectedStep !== undefined && index >= newStepIndex) step.completed = false;
      });
    }

    // Find the current active step (if applicable) and set it inactive, then update the next step properties to make the next step active
    const currentActiveStep = this.steps.find(s => s.active);
    if (currentActiveStep) currentActiveStep.active = false;
    if (this.selectedStep !== undefined) this.selectedStep.active = true;

    // Push changes to UI
    this.cdr.detectChanges();
  }
}
